import React, { useEffect, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Textarea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Checkbox,
  Label,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@roger/ui-components';
import { listPhoRocVisits, updateOrder } from '../../services/api';
import { getOrderPhoPrefillOrdersOrderIdPhoPrefillGet } from '../../services/api/sdk.gen';
import type {
  OrderResponse,
  PhoRocVisitOption,
  TaskStatus,
} from '../../services/api/types.gen';
import { useStore } from '../../store';
import { COMPLETED_STATUSES } from '../../constants/statuses';
import { formatTaskStatusLabel } from './orderUtils';

const DEFAULT_HOMEBOUND_STATEMENT =
  'The patient is homebound because they are unable to leave home without considerable and taxing effort. The patient requires the assistance of another person or the use of supportive devices to leave the home.';

interface PHOOrderFormProps {
  visit: OrderResponse;
  patientId: string;
}

const schema = z.object({
  linked_oasis_visit_id: z.string().min(1, 'ROC visit selection is required'),
  dme_supplies: z.string().optional(),
  risk_profile: z.string().optional(),
  orders_and_treatments: z.string().optional(),
  homebound_certification_statement: z.string().optional(),
  safety_measures: z.string().optional(),
  nutritional_requirements: z.string().optional(),
  goals: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

// Fields prefilled from the linked ROC visit's visit_data. Replaced only when
// the user explicitly loads from a visit (or on first open when empty). Narrative
// fields (orders_and_treatments, homebound_certification_statement) are never
// overwritten by prefill.
const ROC_SOURCED_FIELDS = [
  'dme_supplies',
  'risk_profile',
  'safety_measures',
  'nutritional_requirements',
  'goals',
] as const;

function visitToFormValues(visit: OrderResponse): FormValues {
  const data = (visit.order_data ?? {}) as Record<string, unknown>;
  return {
    linked_oasis_visit_id: visit.linked_oasis_visit_id ?? '',
    dme_supplies: (data.dme_supplies as string) ?? '',
    risk_profile: (data.risk_profile as string) ?? '',
    orders_and_treatments: (data.orders_and_treatments as string) ?? '',
    homebound_certification_statement:
      (data.homebound_certification_statement as string) ?? '',
    safety_measures: (data.safety_measures as string) ?? '',
    nutritional_requirements: (data.nutritional_requirements as string) ?? '',
    goals: (data.goals as string) ?? '',
  };
}

const isRocVisitCompleted = (status: string | null | undefined): boolean =>
  status != null && COMPLETED_STATUSES.has(status as TaskStatus);

export const PHOOrderForm: React.FC<PHOOrderFormProps> = ({
  visit,
  patientId,
}) => {
  const showError = useStore(state => state.showError);
  const [rocVisits, setRocVisits] = useState<PhoRocVisitOption[]>([]);
  const [loadingRocVisits, setLoadingRocVisits] = useState(false);
  const [useStandardHomebound, setUseStandardHomebound] = useState(false);
  const [prefilling, setPrefilling] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialPrefillDoneRef = useRef(false);
  const [prefillSourceVisitId, setPrefillSourceVisitId] = useState<
    string | null
  >(visit.linked_oasis_visit_id ?? null);
  const [confirmLoadOpen, setConfirmLoadOpen] = useState(false);
  const orderDataLastUpdatedRef = useRef<string | null>(
    visit.order_data_last_updated ?? null
  );

  useEffect(() => {
    orderDataLastUpdatedRef.current = visit.order_data_last_updated ?? null;
  }, [visit.id, visit.order_data_last_updated]);

  const {
    control,
    register,
    watch,
    setValue,
    getValues,
    trigger,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: visitToFormValues(visit),
  });

  useEffect(() => {
    const fetchRocVisits = async () => {
      setLoadingRocVisits(true);
      try {
        const res = await listPhoRocVisits({
          path: { patient_id: patientId },
          query: { order_id: visit.id },
        });
        if (res.data) {
          setRocVisits(res.data);
        }
      } catch (err) {
        console.error('Failed to fetch ROC visits:', err);
        showError(
          'Load Failed',
          'Unable to load ROC visits for this order. Please try again.'
        );
      } finally {
        setLoadingRocVisits(false);
      }
    };
    void fetchRocVisits();
  }, [patientId, visit.id]);

  const handleStandardTemplateChange = (checked: boolean) => {
    setUseStandardHomebound(checked);
    if (checked) {
      setValue(
        'homebound_certification_statement',
        DEFAULT_HOMEBOUND_STATEMENT
      );
    }
  };

  const fetchAndApplyPrefill = async (
    rocVisitId: string,
    clearFirst: boolean
  ): Promise<boolean> => {
    setPrefilling(true);
    try {
      const res = await getOrderPhoPrefillOrdersOrderIdPhoPrefillGet({
        path: { order_id: visit.id },
        query: { visit_id: rocVisitId },
      });
      const data = res.data;
      if (!data) return false;
      if (clearFirst) {
        for (const field of ROC_SOURCED_FIELDS) {
          setValue(field, '', { shouldDirty: true });
        }
      }
      const apply = (
        field: (typeof ROC_SOURCED_FIELDS)[number],
        value: unknown
      ) => {
        if (typeof value === 'string' && value) {
          setValue(field, value, { shouldDirty: true });
        }
      };
      apply('dme_supplies', data.dme_supplies);
      apply('safety_measures', data.safety_measures);
      apply('nutritional_requirements', data.nutritional_requirements);
      apply('risk_profile', data.risk_profile);
      apply('goals', data.goals);
      return true;
    } catch (err) {
      console.error('Failed to fetch PHO prefill:', err);
      showError(
        'Prefill Failed',
        'Unable to load prefilled data from the ROC visit. Please try again or fill in fields manually.'
      );
      return false;
    } finally {
      setPrefilling(false);
    }
  };

  const linkedVisitId = watch('linked_oasis_visit_id');
  useEffect(() => {
    if (initialPrefillDoneRef.current) return;
    initialPrefillDoneRef.current = true;
    if (!linkedVisitId) return;
    const values = getValues();
    const rocFieldsEmpty = ROC_SOURCED_FIELDS.every(f => !values[f]?.trim());
    if (rocFieldsEmpty) {
      void fetchAndApplyPrefill(linkedVisitId, false).then(ok => {
        if (ok) setPrefillSourceVisitId(linkedVisitId);
      });
    }
  }, [linkedVisitId]);

  const selectedVisitDiffersFromPrefillSource =
    Boolean(linkedVisitId) && linkedVisitId !== prefillSourceVisitId;

  const hasRocSourcedContent = () =>
    ROC_SOURCED_FIELDS.some(f => Boolean(getValues(f)?.trim()));

  const handleLoadFromRocVisit = async () => {
    if (!linkedVisitId) return;
    const clearFirst = hasRocSourcedContent();
    const ok = await fetchAndApplyPrefill(linkedVisitId, clearFirst);
    if (!ok) return;
    setPrefillSourceVisitId(linkedVisitId);
    setConfirmLoadOpen(false);
  };

  const requestLoadFromRocVisit = () => {
    if (!linkedVisitId) return;
    if (hasRocSourcedContent()) {
      setConfirmLoadOpen(true);
      return;
    }
    void handleLoadFromRocVisit();
  };

  useEffect(() => {
    const subscription = watch(formValues => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(async () => {
        const ok = await trigger();
        if (!ok) return;
        const response = await updateOrder({
          path: { order_id: visit.id },
          body: {
            order_data: { ...formValues, payload_kind: 'cms485' },
            order_data_last_updated: orderDataLastUpdatedRef.current,
          },
        });
        if (response.error) {
          showError('Failed to save order. Please try again.');
          return;
        }
        if (response.data?.order_data_last_updated) {
          orderDataLastUpdatedRef.current =
            response.data.order_data_last_updated;
        }
      }, 1500);
    });
    return () => {
      subscription.unsubscribe();
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [watch, trigger, visit.id, showError]);

  return (
    <Card>
      <CardContent className='space-y-4'>
        <div className='space-y-1.5'>
          <Label>ROC Visit</Label>
          <Controller
            name='linked_oasis_visit_id'
            control={control}
            render={({ field }) => {
              const selectedVisit = rocVisits.find(
                v => v.visit_id === field.value
              );
              const linkedVisitMissingFromOptions =
                Boolean(field.value) &&
                !loadingRocVisits &&
                selectedVisit === undefined;
              const selectedIsIncomplete =
                selectedVisit !== undefined &&
                !isRocVisitCompleted(selectedVisit.status);
              return (
                <>
                  <Select
                    value={field.value}
                    onValueChange={value => {
                      field.onChange(value);
                      const rocFieldsEmpty = ROC_SOURCED_FIELDS.every(
                        f => !getValues(f)?.trim()
                      );
                      if (rocFieldsEmpty && value) {
                        void fetchAndApplyPrefill(value, false).then(ok => {
                          if (ok) setPrefillSourceVisitId(value);
                        });
                      }
                    }}
                  >
                    <SelectTrigger selectSize='sm'>
                      <SelectValue
                        placeholder={
                          loadingRocVisits ? 'Loading...' : 'Select ROC visit'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {rocVisits.map(v => {
                        const isCompleted = isRocVisitCompleted(v.status);
                        const statusLabel =
                          v.status != null
                            ? formatTaskStatusLabel(v.status as TaskStatus)
                            : 'Unknown';
                        return (
                          <SelectItem key={v.visit_id} value={v.visit_id}>
                            <span
                              className={
                                isCompleted
                                  ? undefined
                                  : 'text-muted-foreground'
                              }
                            >
                              {v.display_name}
                              {' · '}
                              {statusLabel}
                              {!isCompleted && ' (not completed)'}
                            </span>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  {linkedVisitMissingFromOptions && (
                    <p className='text-xs text-amber-600'>
                      Linked ROC visit is no longer available for this order.
                      Choose another visit or reload from the current selection.
                    </p>
                  )}
                  {selectedIsIncomplete && (
                    <p className='text-xs text-amber-600'>
                      Source visit is not completed. Prefilled data may be
                      incomplete — finish the ROC first when possible.
                    </p>
                  )}
                </>
              );
            }}
          />
          {linkedVisitId && !prefilling && (
            <div className='flex flex-col gap-2 sm:flex-row sm:items-center'>
              <p className='text-xs text-muted-foreground'>
                {selectedVisitDiffersFromPrefillSource
                  ? 'Changing the linked visit does not update the fields below until you reload from the selected ROC visit.'
                  : 'You can prefill form content from the linked ROC visit. Note: existing values will be overwritten.'}
              </p>
              <Button
                type='button'
                variant='outline'
                size='sm'
                className='shrink-0'
                onClick={requestLoadFromRocVisit}
              >
                Prefill from ROC visit
              </Button>
            </div>
          )}
          {prefilling && (
            <p className='text-xs text-muted-foreground'>
              Loading prefilled data from ROC visit…
            </p>
          )}
          <Dialog open={confirmLoadOpen} onOpenChange={setConfirmLoadOpen}>
            <DialogContent className='max-w-md'>
              <DialogHeader>
                <DialogTitle>Replace prefilled fields?</DialogTitle>
                <DialogDescription>
                  Loading from the selected ROC visit will replace DME &
                  Supplies, Patient Risk Profile, Safety Measures, Nutritional
                  Requirements, and Goals. Orders & Treatments and the homebound
                  statement will not be changed.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  onClick={() => setConfirmLoadOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type='button'
                  size='sm'
                  onClick={() => void handleLoadFromRocVisit()}
                  disabled={prefilling}
                >
                  Reload from ROC visit
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          {errors.linked_oasis_visit_id && (
            <p className='text-xs text-red-500'>
              {errors.linked_oasis_visit_id.message}
            </p>
          )}
        </div>

        <div className='space-y-1.5'>
          <Label>DME & Supplies</Label>
          <Textarea
            {...register('dme_supplies')}
            placeholder='Enter DME and supplies'
            textareaSize='sm'
          />
        </div>

        <div className='space-y-1.5'>
          <Label>Patient Risk Profile</Label>
          <Textarea
            {...register('risk_profile')}
            placeholder='Enter risk profile'
            textareaSize='sm'
          />
        </div>

        <div className='space-y-1.5'>
          <Label>Orders & Treatments</Label>
          <Textarea
            {...register('orders_and_treatments')}
            placeholder='Enter orders and treatments'
            textareaSize='sm'
          />
        </div>

        <div className='space-y-1.5'>
          <Label>Homebound Certification Statement</Label>
          <div className='flex items-center gap-2 mb-1'>
            <Checkbox
              checked={useStandardHomebound}
              onCheckedChange={checked =>
                handleStandardTemplateChange(checked === true)
              }
            />
            <span className='text-xs text-muted-foreground'>
              Use standard template
            </span>
          </div>
          <Textarea
            {...register('homebound_certification_statement')}
            placeholder='Enter homebound certification statement'
            textareaSize='sm'
          />
        </div>

        <div className='space-y-1.5'>
          <Label>Safety Measures</Label>
          <Textarea
            {...register('safety_measures')}
            placeholder='Enter safety measures'
            textareaSize='sm'
          />
        </div>

        <div className='space-y-1.5'>
          <Label>Nutritional Requirements</Label>
          <Textarea
            {...register('nutritional_requirements')}
            placeholder='Enter nutritional requirements'
            textareaSize='sm'
          />
        </div>

        <div className='space-y-1.5'>
          <Label>Goals & Measurable Outcomes</Label>
          <Textarea
            {...register('goals')}
            placeholder='Enter goals and measurable outcomes'
            textareaSize='sm'
          />
        </div>
      </CardContent>
    </Card>
  );
};
